<template>
  <v-container>
    <v-layout row justify-center>
      <v-flex xs12 sm10 md8 lg6>
        <v-card>
          <v-card-title class="headline primary white--text">
            Actualización de INE
          </v-card-title>
          <v-card-text>
            <v-dialog v-model="cropDialog" persistent scrollable max-width="700px">
              <v-card>
                <v-toolbar dark color="primary">
                  <v-toolbar-title>Recortar imagen</v-toolbar-title>
                </v-toolbar>
                <v-card-text>
                  <clipper-fixed
                    v-if="cropSourceUrl"
                    ref="imageCropper"
                    :src="cropSourceUrl"
                    :rotate="degrees"
                    :ratio="ratio"
                  ></clipper-fixed>
                  <v-alert v-if="cropError" :value="true" type="error" outline>
                    {{ cropError }}
                  </v-alert>
                  <v-radio-group v-model="ratio" row label="Proporción" :mandatory="false">
                    <v-radio label="1:1" :value="1"></v-radio>
                    <v-radio label="3:2" :value="1.5"></v-radio>
                    <v-radio label="4:3" :value="1.3333333333333333"></v-radio>
                    <v-radio label="16:9" :value="1.7777777777777777"></v-radio>
                  </v-radio-group>
                  <v-slider
                    v-model="degrees"
                    label="Rotación"
                    step="1"
                    max="180"
                    min="-180"
                    thumb-label="always"
                  ></v-slider>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn flat @click="cancelCrop">Cancelar</v-btn>
                  <v-btn color="primary" @click="applyCrop">Recortar y usar</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-progress-linear v-if="loadingStatus" indeterminate color="primary"></v-progress-linear>

            <v-alert v-if="statusMessage" :value="true" :type="statusMessageType" outline>
              {{ statusMessage }}
            </v-alert>

            <template v-if="requestStatus === 'PENDING'">
              <p>Selecciona una imagen para cada lado de tu INE y una selfie para validar tu rostro. La selfie solo se usa para la validación y no se envía para actualizar tu INE.</p>
              <v-form ref="form" @submit.prevent="submitPhotos">
                <v-layout row wrap>
                  <v-flex xs12>
                    <v-text-field
                      :value="frontFile ? frontFile.name : ''"
                      label="Subir foto INE - Frente"
                      prepend-icon="badge"
                      readonly
                      :rules="[requiredFile]"
                      @click="openFilePicker('front')"
                    ></v-text-field>
                    <input
                      ref="frontInput"
                      type="file"
                      accept="image/*"
                      hidden
                      @change="selectFile($event, 'front')"
                    >
                  </v-flex>
                  <v-flex v-if="frontPreview" xs12>
                    <v-img :src="frontPreview" max-height="220" contain></v-img>
                  </v-flex>
                  <v-flex xs12>
                    <v-text-field
                      :value="backFile ? backFile.name : ''"
                      label="Subir foto INE - Reverso"
                      prepend-icon="image"
                      readonly
                      :rules="[requiredFile]"
                      @click="openFilePicker('back')"
                    ></v-text-field>
                    <input
                      ref="backInput"
                      type="file"
                      accept="image/*"
                      hidden
                      @change="selectFile($event, 'back')"
                    >
                  </v-flex>
                  <v-flex v-if="backPreview" xs12>
                    <v-img :src="backPreview" max-height="220" contain></v-img>
                  </v-flex>
                  <v-flex xs12>
                    <v-text-field
                      :value="selfieFile ? selfieFile.name : ''"
                      label="Selfie para validar rostro"
                      prepend-icon="face"
                      readonly
                      :rules="[requiredFile]"
                      @click="openFilePicker('selfie')"
                    ></v-text-field>
                    <input
                      ref="selfieInput"
                      type="file"
                      accept="image/*"
                      capture="user"
                      hidden
                      @change="selectFile($event, 'selfie')"
                    >
                  </v-flex>
                </v-layout>
                <v-btn
                  type="button"
                  color="primary"
                  outline
                  :disabled="!frontFile || !selfieFile || faceComparisonLoading || submitting"
                  :loading="faceComparisonLoading"
                  @click="compareFaces"
                >
                  Validar rostro
                </v-btn>
                <v-alert
                  v-if="faceComparisonError"
                  :value="true"
                  type="error"
                  outline
                  class="mt-3"
                >
                  {{ faceComparisonError }}
                </v-alert>
                <v-alert
                  v-if="faceValidated"
                  :value="true"
                  type="success"
                  outline
                  class="mt-3"
                >
                  Rostro validado correctamente. Ya puedes enviar las fotos de tu INE.
                </v-alert>
                <v-btn
                  type="submit"
                  color="primary"
                  :loading="submitting"
                  :disabled="submitting || requestStatus !== 'PENDING' || !frontFile || !backFile || !faceValidated"
                >
                  Enviar fotos
                </v-btn>
              </v-form>
            </template>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import Vue from 'vue'
import axios from 'axios'
import VuejsClipper from 'vuejs-clipper'

Vue.use(VuejsClipper, {
  components: {
    clipperFixed: 'clipper-fixed'
  }
})

const API_BASE_URL = 'https://empresa-service.intrare.app'
const FACE_COMPARISON_URL = 'https://microservices.intrare.app/identification/compare/'
const FACE_COMPARISON_API_KEY = '63f6f7729962518de2073625d1e61be17d5f99b5'

export default {
  name: 'ActualizarIne',
  data () {
    return {
      requestStatus: null,
      loadingStatus: true,
      submitting: false,
      cropDialog: false,
      cropSide: null,
      cropSourceUrl: '',
      cropPendingFile: null,
      cropError: '',
      degrees: 0,
      ratio: 1,
      frontFile: null,
      frontPreview: '',
      backFile: null,
      backPreview: '',
      selfieFile: null,
      faceComparisonResult: null,
      faceComparisonLoading: false,
      faceComparisonError: '',
      statusMessage: '',
      statusMessageType: 'info'
    }
  },
  computed: {
    faceValidated () {
      return !!(
        this.faceComparisonResult &&
        this.faceComparisonResult.success &&
        this.faceComparisonResult.match
      )
    }
  },
  created () {
    this.checkRequestStatus()
  },
  methods: {
    requestUrl () {
      return `${API_BASE_URL}/public/ine-update/${encodeURIComponent(this.$route.params.token)}/`
    },
    async checkRequestStatus () {
      this.loadingStatus = true
      this.statusMessage = ''

      try {
        const response = await axios.get(this.requestUrl())
        const status = response.data && response.data.status

        if (status === 'COMPLETED') {
          this.requestStatus = status
          this.statusMessage = 'Esta solicitud ya fue completada. No es posible volver a subir las fotos de la INE.'
          this.statusMessageType = 'success'
        } else if (status === 'PENDING') {
          this.requestStatus = status
        } else {
          this.requestStatus = status || null
          this.statusMessage = 'Esta liga ya no está disponible para actualizar las fotos de la INE.'
          this.statusMessageType = 'error'
        }
      } catch (error) {
        this.requestStatus = null
        if (error.response && error.response.status === 410) {
          this.statusMessage = 'Esta liga ya fue utilizada, venció o dejó de estar vigente. No es posible subir fotos nuevamente.'
          this.statusMessageType = 'warning'
        } else {
          this.statusMessage = 'No se pudo validar la liga. Verifica tu conexión e intenta cargar la página nuevamente.'
          this.statusMessageType = 'error'
        }
      } finally {
        this.loadingStatus = false
      }
    },
    requiredFile (file) {
      return !!file || 'Este archivo es requerido.'
    },
    openFilePicker (side) {
      if (this.requestStatus !== 'PENDING' || this.submitting || this.faceComparisonLoading) return
      const input = {
        front: this.$refs.frontInput,
        back: this.$refs.backInput,
        selfie: this.$refs.selfieInput
      }[side]
      input.click()
    },
    selectFile (event, side) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return

      if (!file.type || !file.type.startsWith('image/')) {
        this.statusMessage = 'Selecciona un archivo de imagen válido.'
        this.statusMessageType = 'error'
        return
      }

      this.statusMessage = ''
      if (side === 'selfie') {
        this.selfieFile = file
        this.faceComparisonResult = null
        this.faceComparisonError = ''
        if (this.$refs.form) this.$refs.form.validate()
        return
      }

      this.cropSide = side
      this.cropPendingFile = file
      this.cropSourceUrl = ''
      this.cropError = ''
      this.degrees = 0
      this.ratio = 1

      const reader = new FileReader()
      reader.onload = () => {
        if (typeof reader.result !== 'string') {
          this.cropError = 'No se pudo cargar la imagen seleccionada.'
          this.cropDialog = true
          return
        }
        this.cropSourceUrl = reader.result
        this.cropDialog = true
      }
      reader.onerror = () => {
        this.statusMessage = 'No se pudo leer la imagen seleccionada. Intenta con otro archivo.'
        this.statusMessageType = 'error'
      }
      reader.readAsDataURL(file)
    },
    cancelCrop () {
      this.cropDialog = false
      this.cropSide = null
      this.cropPendingFile = null
      this.cropSourceUrl = ''
      this.cropError = ''
    },
    applyCrop () {
      if (!this.cropPendingFile || !this.cropSide || !this.$refs.imageCropper) {
        this.cropError = 'No se pudo preparar la imagen para recortarla. Selecciónala nuevamente.'
        return
      }

      try {
        const canvas = this.$refs.imageCropper.clip()
        if (!canvas) {
          this.cropError = 'No se pudo recortar la imagen. Intenta nuevamente.'
          return
        }

        const croppedImage = canvas.toDataURL('image/jpeg', 0.9)
        const fileName = this.cropPendingFile.name.replace(/\.[^.]+$/, '') || this.cropPendingFile.name
        const croppedFile = this.dataURLtoFile(croppedImage, `${fileName}.jpg`)

        if (this.cropSide === 'front') {
          this.frontFile = croppedFile
          this.frontPreview = croppedImage
        } else {
          this.backFile = croppedFile
          this.backPreview = croppedImage
        }

        this.faceComparisonResult = null
        this.faceComparisonError = ''
        this.cancelCrop()
        if (this.$refs.form) this.$refs.form.validate()
      } catch (error) {
        this.cropError = 'Ocurrió un error al recortar la imagen. Intenta nuevamente.'
      }
    },
    dataURLtoFile (dataUrl, filename) {
      const parts = dataUrl.split(',')
      const mime = parts[0].match(/:(.*?);/)[1]
      const binary = atob(parts[1])
      const bytes = new Uint8Array(binary.length)
      for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index)
      }
      return new File([bytes], filename, { type: mime })
    },
    async compareFaces () {
      if (this.requestStatus !== 'PENDING' || !this.frontFile || !this.selfieFile || this.faceComparisonLoading || this.submitting) return

      this.faceComparisonLoading = true
      this.faceComparisonResult = null
      this.faceComparisonError = ''
      const formData = new FormData()
      formData.append('id_image', this.frontFile, this.frontFile.name || 'ine.jpg')
      formData.append('profile_image', this.selfieFile, this.selfieFile.name || 'selfie.jpg')

      try {
        const response = await axios.post(FACE_COMPARISON_URL, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-API-Key': FACE_COMPARISON_API_KEY
          }
        })

        this.faceComparisonResult = response.data
        if (!this.faceValidated) {
          this.faceComparisonError = (response.data && response.data.message) || 'Las imágenes no coinciden. Verifica la foto del frente de tu INE y toma otra selfie.'
        }
      } catch (error) {
        const responseData = error.response && error.response.data
        this.faceComparisonError = (responseData && (
          responseData.message ||
          responseData.detail ||
          (typeof responseData.error === 'string' ? responseData.error : responseData.error && responseData.error.message)
        )) || 'No se pudo validar el rostro. Verifica tu conexión e intenta nuevamente.'
      } finally {
        this.faceComparisonLoading = false
      }
    },
    async submitPhotos () {
      if (
        this.requestStatus !== 'PENDING' ||
        this.submitting ||
        !this.frontFile ||
        !this.backFile ||
        !this.faceValidated
      ) return

      if (!this.$refs.form.validate() || !this.frontFile || !this.backFile) return

      this.submitting = true
      this.statusMessage = ''
      const formData = new FormData()
      formData.append('ine_frente', this.frontFile)
      formData.append('ine_atras', this.backFile)

      try {
        await axios.post(this.requestUrl(), formData)
        this.requestStatus = 'COMPLETED'
        this.statusMessage = 'Las fotos de tu INE se actualizaron correctamente.'
        this.statusMessageType = 'success'
        this.frontFile = null
        this.frontPreview = ''
        this.backFile = null
        this.backPreview = ''
        this.selfieFile = null
        this.faceComparisonResult = null
      } catch (error) {
        if (error.response && error.response.status === 410) {
          this.requestStatus = 'EXPIRED'
          this.statusMessage = 'Esta liga ya no está vigente. No es posible enviar las fotos nuevamente.'
          this.statusMessageType = 'warning'
        } else {
          this.statusMessage = 'No se pudieron enviar las fotos. Intenta nuevamente.'
          this.statusMessageType = 'error'
        }
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>
